import React from "react";
import i18n from "../../../i18n/i18n";
import Table from "../../../common/components/shared/table/table";
import Loader from "../../../common/components/shared/loader/loader";
import {Api} from "../../../helpers/api";
import {HttpError} from "../../../common/components/shared/httpError/httpError";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../common/models/httpError";
import {
    LightBoardModel,
    LightBoardSendMessageModel,
    ManualOperatingSignCode,
    OperatingModeType
} from "../../models/lightBoard";
import {LightBoardService} from "../../services/lightBoardService";
import "./lightBoardList.scss";
import {Popup} from "../../../common/components/shared/popup/popup";
import TextInput from "../../../common/components/shared/textInput/textInput";
import {ValidatedValue} from "../../../helpers/validatedValue";
import DropDown from "../../../common/components/shared/dropDown/dropDown";
import {DropDownModel} from "../../../common/models/dropDown";
import ReloadButton from "../../../common/components/shared/reloadButton/reloadButton";
import {DropDownUtils} from "../../../helpers/dropDownUtils";

interface LightBoardListState {
    lightBoardListData: LightBoardModel[] | undefined | null;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    changeAutoModeResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    changeManualModeResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    sendMessageForLightBoardResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    displayManualOperatingPopup: boolean;
    selectedElementId: string;
    manualOperatingMessage: ValidatedValue<string>;
    manualOperatingSign: ValidatedValue<string>;
    currentPage: number;
    signCodes: ManualOperatingSignCode[];
    signCodesResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    isTableLoader: boolean;
    isWaiting: boolean;
    numberOfElements: number;
}

export default class LightBoardList extends React.Component <any, LightBoardListState> {
    constructor(props: any) {
        super(props);

        this.state = {
            lightBoardListData: undefined,
            response: undefined,
            changeAutoModeResponse: undefined,
            changeManualModeResponse: undefined,
            sendMessageForLightBoardResponse: undefined,
            displayManualOperatingPopup: false,
            selectedElementId: "",
            manualOperatingMessage: new ValidatedValue<string>(""),
            manualOperatingSign: new ValidatedValue<string>(""),
            currentPage: 0,
            signCodes: [],
            isTableLoader: false,
            isWaiting: false,
            numberOfElements: 0,
            signCodesResponse: undefined
        };

        const manualOperatingValidator = {
            name: "manualOperatingValidator",
            fn: () => {
                return this.state.manualOperatingSign.value !== "" || this.state.manualOperatingMessage.value !== "";
            },
            errorMessage: () => `${i18n.t("place.lightBoards.manualOperatingPopup.required")}`
        };
        this.state.manualOperatingMessage.addValidators(manualOperatingValidator);
        this.state.manualOperatingSign.addValidators(manualOperatingValidator);
    }

    get lightBoardListData() {
        return this.state.lightBoardListData;
    }

    public async componentDidMount() {
        await this.getAllLightBoards();

        await this.getSignCodes();
    }

    public render(): JSX.Element {
        return (
            <div className="light-board-list">
                {this.renderWrapperContent()}
                {this.renderManualOperatingPopup()}
            </div>
        );
    }

    private renderActions(element: LightBoardModel): JSX.Element {
        const elementId = element.id;
        const hasManualMode = element.operatingMode === OperatingModeType.MANUAL;

        return (
            <>
                {elementId && (
                    <>
                        <span
                            className="action"
                            data-testid={`change-to-manual-mode-button-${elementId}`}
                            onClick={() => {
                                this.state.manualOperatingMessage.setValue("");
                                this.state.manualOperatingMessage.clearValidation();
                                this.state.manualOperatingSign.setValue("");
                                this.state.manualOperatingSign.clearValidation();

                                this.setState({
                                    displayManualOperatingPopup: true,
                                    selectedElementId: elementId,
                                    sendMessageForLightBoardResponse: undefined
                                });
                            }}
                        >
                            {i18n.t("place.lightBoards.actions.changeToManualMode")}
                        </span>

                        <span
                            className={`action ${hasManualMode ? "" : "inactive"}`}
                            data-testid={`change-to-auto-mode-button-${elementId}`}
                            onClick={async () => {
                                this.setState({isTableLoader: true});
                                if (hasManualMode) {
                                    await this.changeToAutoMode(elementId);
                                }
                                this.setState({isTableLoader: false});
                            }}
                        >
                            {i18n.t("place.lightBoards.actions.changeToAutoMode")}
                        </span>
                    </>
                )}
            </>
        );
    }

    private renderHeader() {
        return (
            <div className="header">
                <div className="actions">
                    <ReloadButton
                        reloadFunc={this.reload.bind(this)}
                        testId="light-board-list-reload-btn"
                    />
                </div>
            </div>
        );
    }

    private renderSuccessContent(headers: string[], properties: string[], lightBoardListData: LightBoardModel[]) {
        return (
            <div>
                <Table
                    headers={headers}
                    rows={lightBoardListData}
                    properties={properties}
                    testId="light-board-table"
                    actions={this.renderActions.bind(this)}
                    pageable={true}
                    renderCell={this.renderCell.bind(this)}
                    renderEmptyCell={true}
                    currentPage={this.state.currentPage}
                    numberOfElements={this.state.numberOfElements}
                    isOldPagination
                    isTableLoader={this.state.isTableLoader}
                />

                {!Api.isSuccess(this.state.changeAutoModeResponse) && (
                    <HttpError
                        response={this.state.changeAutoModeResponse}
                        cause={i18n.t("place.lightBoards.changeMessageErrorSuffix")}
                    />
                )}
            </div>
        );
    }

    private renderWrapperContent() {
        if (!Api.isSuccess(this.state.response)) {
            return (
                <HttpError
                    response={this.state.response}
                    cause={i18n.t("place.lightBoards.retrievalErrorSuffix")}
                />
            );
        } else {
            if (this.lightBoardListData === undefined) {
                return <Loader/>;
            } else {
                const properties = ["location", "operatingMode", "currentMessage"];
                const headers = properties.map(key => i18n.t("place.lightBoards.headers." + key));

                return (
                    <>
                        {this.renderHeader()}
                        <div className="light-board-list-content">
                            {this.lightBoardListData && this.lightBoardListData!.length && this.lightBoardListData!.length > 0 ? (
                                this.renderSuccessContent(headers, properties, this.lightBoardListData)
                            ) : (
                                <Table
                                    headers={headers}
                                    rows={[]}
                                    properties={properties}
                                    testId="light-board-table"
                                    currentPage={this.state.currentPage}
                                    numberOfElements={this.state.numberOfElements}
                                />
                            )}

                        </div>
                    </>
                );
            }
        }
    }

    private renderCell(row: any, property: string): JSX.Element {
        if (property === "operatingMode") {
            return <>{i18n.t(`place.lightBoards.operatingModes.${row[property]}`)}</>;
        } else if (property === "currentMessage") {
            return <>{this.renderMessageForManualMode(row)}</>;
        }

        return row[property];
    }

    private renderMessageForManualMode(row: LightBoardModel): JSX.Element {
        const currentMessage = row.currentMessage;
        let sign = row.sign ? <>{i18n.t("place.lightBoards.manualOperatingSign")}: {this.manualOperatingSignTranslator(row.sign)}</> : "";
        sign = row.currentMessage ? <><br/>{sign}</> : sign;

        if (currentMessage || sign) {
            return <>{currentMessage}{sign}</>;
        }

        return <>-</>;
    }

    private renderManualOperatingPopup() {
        const content = <div className="manual-operating-popup-content">
            <TextInput
                className="manual-operating-message"
                validatedValue={this.state.manualOperatingMessage}
                label={i18n.t("place.lightBoards.manualOperatingPopup.manualOperatingMessage")}
                testId="manual-operating-message"
                maxRows={4}
                onChange={(value) => {
                    this.state.manualOperatingMessage.setValue(value);
                    this.formOnPopupIsValid;
                }}
            />
            <DropDown
                className="manual-operating-sign"
                validatedValue={this.state.manualOperatingSign}
                items={this.manualOperatingSignDropDownMap}
                label={i18n.t("place.lightBoards.manualOperatingPopup.manualOperatingSign")}
                testId="manual-operating-sign"
                onChange={(value) => {
                    this.state.manualOperatingSign.setValue(value);
                    this.formOnPopupIsValid;
                }}
                withEmptyValue
                withoutSort
                error={!Api.isSuccess(this.state.signCodesResponse) ? i18n.t("errorMessages.signCodesErrorSuffix") : ""}
                reloadFunc={() => this.getSignCodes()}
            />
        </div>;

        return (
            <Popup
                open={this.state.displayManualOperatingPopup}
                className="manual-operating-popup"
                header={i18n.t("place.lightBoards.manualOperatingPopup.header")}
                content={content}
                action={async () => await this.changeToManualMode()}
                cancel={() => this.setState({displayManualOperatingPopup: false})}
                testId="manual-operating-popup"
                httpError={<>
                    {!Api.isSuccess(this.state.changeManualModeResponse) && (
                        <HttpError
                            response={this.state.changeManualModeResponse}
                            cause={i18n.t("place.lightBoards.changeMessageErrorSuffix")}
                        />
                    )}
                    {!Api.isSuccess(this.state.sendMessageForLightBoardResponse) && (
                        <HttpError
                            response={this.state.sendMessageForLightBoardResponse}
                            cause={i18n.t("place.lightBoards.sendMessageErrorSuffix")}
                        />
                    )}
                </>}
                isWaiting={this.state.isWaiting}
            />
        );
    }

    private async changeToAutoMode(elementId: string) {
        this.setState({changeAutoModeResponse: undefined, response: undefined});

        const changeAutoModeResponse = await LightBoardService.switchToAutoMode(elementId);

        this.setState({changeAutoModeResponse, selectedElementId: ""});

        const response = await LightBoardService.getAll();
        this.setState({lightBoardListData: response?.data?.data || null, response});
    }

    private get formOnPopupIsValid() {
        this.state.manualOperatingMessage.validate();
        this.state.manualOperatingSign.validate();

        return this.state.manualOperatingMessage.isValid && this.state.manualOperatingSign.isValid;
    }

    private async changeToManualMode() {
        if (!this.formOnPopupIsValid) {
            return;
        }

        await this.setState({
            changeManualModeResponse: undefined,
            sendMessageForLightBoardResponse: undefined,
            response: undefined,
            isWaiting: true
        });

        const newMessageForLightBoard = {
            message: this.state.manualOperatingMessage.value,
            sign: this.state.manualOperatingSign.value
        };

        await this.sendMessageForLightBoard(newMessageForLightBoard);

        await this.getAllLightBoards();

        this.setState({isWaiting: false});
    }

    private async sendMessageForLightBoard(message: LightBoardSendMessageModel) {
        this.setState({
            sendMessageForLightBoardResponse: undefined
        });

        const sendMessageForLightBoardResponse = await LightBoardService.sendMessageForLightBoard(this.state.selectedElementId, message);

        this.setState({
            sendMessageForLightBoardResponse
        });

        if (Api.isSuccess(sendMessageForLightBoardResponse) && sendMessageForLightBoardResponse) {
            this.setState({displayManualOperatingPopup: false, selectedElementId: ""});
        }
    }

    private manualOperatingSignTranslator(signCode: ManualOperatingSignCode | string): string {
        switch (signCode) {
            case "RIGHT_TURN":
            case "LEFT_TURN":
            case "AHEAD":
            case "NO_ENTRY":
                return i18n.t(`place.lightBoards.signCodes.${signCode}`);
            default:
                return signCode;
        }
    }

    public get manualOperatingSignDropDownMap(): DropDownModel[] {
        return DropDownUtils.listToDropDownMap(this.state.signCodes, "id", "name", this.manualOperatingSignTranslator, true);
    }

    private async getAllLightBoards() {
        this.setState({
            isTableLoader: true
        });

        const response = await LightBoardService.getAll();

        this.setState({
            lightBoardListData: response?.data?.data || null,
            response,
            currentPage: response?.data?.currentPage,
            isTableLoader: false,
            numberOfElements: response?.data?.numberOfElements
        });
    }

    private async reload() {
        this.setState({lightBoardListData: undefined});
        await this.getAllLightBoards();
    }

    private async getSignCodes() {
        const signCodesResponse = await LightBoardService.getAllSings();

        this.setState({
            signCodes: (signCodesResponse)?.data || [],
            signCodesResponse
        });
    }
}
